/**
 * Save- and launch-time plan notices, as a readable block.
 *
 * These used to render as inline `<span>`s wedged into the review header's
 * right-hand button cluster, with every message after the first hidden in a
 * native `title` tooltip. Three sanity warnings — each a full sentence about a
 * different campaign — therefore appeared as one truncated line spilling amber
 * text across the header, plus an OS tooltip box painted over the toolbar with
 * all three run together into a paragraph. The content was fine; there was
 * nowhere for it to go.
 *
 * So: its own full-width strip under the header, one message per row, severity
 * on the strip rather than inferred from colour alone, and the list collapsed
 * past a few so a ten-warning plan does not push the grid off screen.
 *
 * SEVERITY IS THE ORDER. Errors first — an error means the thing the user asked
 * for did not happen — then warnings, which mean it did happen and something at
 * launch will differ from what is on screen. A dismissed strip stays dismissed
 * until the next save produces new notices; that is the caller's state, not
 * ours.
 */

import { useState } from 'react';

const VISIBLE = 3;

const SEVERITY = {
  error: {
    cls: 'err',
    icon: '⚠',
    heading: (n) => (n === 1 ? "Couldn't save" : `${n} problems`),
  },
  warning: {
    cls: 'warn',
    icon: '⚠',
    // Says the save SUCCEEDED, because that is the first question a warning
    // strip raises and the old copy ("Saved with N plan warnings") was the one
    // part worth keeping.
    heading: (n) => `Saved — ${n} plan warning${n === 1 ? '' : 's'}`,
  },
  info: {
    cls: 'info',
    icon: 'ℹ',
    heading: (n) => `${n} note${n === 1 ? '' : 's'}`,
  },
};

/** A notice's text, whatever shape the producer used. */
const textOf = (n) => {
  if (n == null) return '';
  if (typeof n === 'string') return n;
  return String(n.message ?? n.detail ?? n.error ?? n.code ?? '');
};

/**
 * @param {Array} notices [{ severity?: 'error'|'warning'|'info', message, code?, row? }]
 *                        or bare strings, which are treated as the `severity`
 *                        passed in `defaultSeverity`.
 */
export function PlanNotices({ notices, defaultSeverity = 'warning', onDismiss, title: heading }) {
  const [expanded, setExpanded] = useState(false);

  const items = (Array.isArray(notices) ? notices : [notices])
    .filter(Boolean)
    .map((n) => ({
      severity: (typeof n === 'object' && n.severity) || defaultSeverity,
      text: textOf(n),
      // The entity a message is about, when the producer named one. Rendered as
      // a lead-in so a reader can scan which campaign each line concerns without
      // parsing the sentence.
      subject: (typeof n === 'object' && (n.row || n.entity || n.name)) || null,
      code: (typeof n === 'object' && n.code) || null,
    }))
    .filter((n) => n.text);

  if (!items.length) return null;

  // One strip, at the highest severity present — two stacked strips for one save
  // reads as two separate failures.
  const level = items.some((n) => n.severity === 'error') ? 'error'
    : items.some((n) => n.severity === 'warning') ? 'warning'
      : 'info';
  const cfg = SEVERITY[level] || SEVERITY.warning;

  const shown = expanded ? items : items.slice(0, VISIBLE);
  const hidden = items.length - shown.length;

  return (
    <div className={`clw-notices ${cfg.cls}`} role={level === 'error' ? 'alert' : 'status'}>
      <div className="clw-notices-head">
        <span className="clw-notices-icon" aria-hidden="true">{cfg.icon}</span>
        <strong className="clw-notices-title">{heading || cfg.heading(items.length)}</strong>
        {onDismiss && (
          <button
            type="button"
            className="clw-notices-dismiss"
            onClick={onDismiss}
            title="Dismiss"
            aria-label="Dismiss notices"
          >×</button>
        )}
      </div>
      <ul className="clw-notices-list">
        {shown.map((n, i) => (
          <li key={`${n.code || 'n'}-${i}`}>
            {n.subject && <span className="clw-notices-subject">{n.subject}</span>}
            {n.text}
          </li>
        ))}
      </ul>
      {hidden > 0 && (
        <button type="button" className="clw-notices-more" onClick={() => setExpanded(true)}>
          Show {hidden} more
        </button>
      )}
      {expanded && items.length > VISIBLE && (
        <button type="button" className="clw-notices-more" onClick={() => setExpanded(false)}>
          Show less
        </button>
      )}
    </div>
  );
}

export default PlanNotices;
